import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { Insurance, Provider, ProviderGroup } from "../types";
import { api } from "../utils/utils";

interface ProviderState {
  providers: Provider[];
  providerGroups: ProviderGroup[];
  insuranceOptions: Insurance[];
  loading: boolean;
  error: string | null;
}

const initialState: ProviderState = {
  providers: [],
  providerGroups: [],
  insuranceOptions: [],
  loading: false,
  error: null,
};

export const fetchProviders = createAsyncThunk<Provider[], string>(
  "provider/fetchProviders",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/providers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue("Failed to fetch providers");
    }
  }
);

export const addProvider = createAsyncThunk<{ message: string }, { provider: Partial<Provider>, token: string }>(
  "provider/addProvider",
  async ({ provider, token }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/providers`,
        { ...provider },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await dispatch(fetchProviders(token));
      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue("Failed to add provider");
    }
  }
);

export const updateProvider = createAsyncThunk<
  { message: string },
  { providerId: string, provider: Partial<Provider>, token: string }
>(
  "provider/updateProvider",
  async ({ providerId, provider, token }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/providers/${providerId}`,
        { ...provider },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await dispatch(fetchProviders(token));
      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue("Failed to update provider");
    }
  }
);

export const deleteProvider = createAsyncThunk<
  void,
  { providerId: string, token: string }
>(
  "provider/deleteProvider",
  async ({ providerId, token }, { rejectWithValue, dispatch }) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/providers/${providerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Refetch both lists: groups reference providers by ehrProviderId, so a
      // provider deletion can leave dangling references that the groups view
      // surfaces. Pulling both keeps the UI consistent regardless of whether
      // the backend cascades.
      await Promise.all([
        dispatch(fetchProviders(token)),
        dispatch(fetchProviderGroups(token)),
      ]);
    } catch (error) {
      console.log(error);
      return rejectWithValue("Failed to delete provider");
    }
  }
);

export const fetchProviderGroups = createAsyncThunk<ProviderGroup[], string>(
  "provider/fetchProviderGroups",
  async (token, { rejectWithValue }) => {
    try {
      const response = await api.get(`/providers/groups`, token);
      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue("Failed to fetch provider groups");
    }
  }
);

export const addProviderGroup = createAsyncThunk<ProviderGroup, { group: Partial<ProviderGroup>, token: string }>(
  "provider/addProviderGroup",
  async ({ group, token }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/providers/groups`, token, { ...group });
      await dispatch(fetchProviderGroups(token));
      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue("Failed to add provider group");
    }
  }
);

export const updateProviderGroup = createAsyncThunk<
  ProviderGroup,
  { providerGroupId: string, group: Partial<ProviderGroup>, token: string }
>(
  "provider/updateProviderGroup",
  async ({ providerGroupId, group, token }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.patch(`/providers/groups/${providerGroupId}`, token, { ...group });
      await dispatch(fetchProviderGroups(token));
      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue("Failed to update provider group");
    }
  }
);

export const deleteProviderGroup = createAsyncThunk<
  void,
  { providerGroupId: string, token: string }
>(
  "provider/deleteProviderGroup",
  async ({ providerGroupId, token }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/providers/groups/${providerGroupId}`, token);
      await dispatch(fetchProviderGroups(token));
    }
    catch (error) {
      console.log(error);
      return rejectWithValue("Failed to delete provider group");
    }
  }
);

export const fetchInsurances = createAsyncThunk<Insurance[], string>(
  "provider/fetchInsurances",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/insurance`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.insurances;
    } catch (error) {
      console.log(error);
      return rejectWithValue("Failed to fetch insurances");
    }
  }
);

const providerSlice = createSlice({
  name: "provider",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchProviders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProviders.fulfilled, (state, action) => {
      state.loading = false;
      state.providers = action.payload;
    });
    builder.addCase(fetchProviders.rejected, (state) => {
      state.loading = false;
      state.error = "Failed to fetch providers";
    });

    builder.addCase(addProvider.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addProvider.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(addProvider.rejected, (state) => {
      state.loading = false;
      state.error = "Failed to add provider";
    });

    builder.addCase(updateProvider.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateProvider.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateProvider.rejected, (state, action) => {
      state.loading = false;
      state.error = "Failed to update provider";
    });

    builder.addCase(deleteProvider.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteProvider.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteProvider.rejected, (state) => {
      state.loading = false;
      state.error = "Failed to delete provider";
    });
    builder.addCase(fetchProviderGroups.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProviderGroups.fulfilled, (state, action) => {
      state.loading = false;
      state.providerGroups = action.payload;
    });
    builder.addCase(fetchProviderGroups.rejected, (state) => {
      state.loading = false;
      state.error = "Failed to fetch provider groups";
    });
    builder.addCase(fetchInsurances.fulfilled, (state, action) => {
      state.insuranceOptions = action.payload;
    });
    builder.addCase(fetchInsurances.rejected, (state) => {
      state.error = "Failed to fetch insurance options";
    });
  },
});

export default providerSlice.reducer;